(() => {
  if (window.MagrionDimensionPicker) return;

  window.MagrionDimensionPicker = {
    productCache: new Map(),
    activeFilters: new Map()
  };

  const state = window.MagrionDimensionPicker;

  const normalize = (value) => {
    return String(value ?? '')
      .trim()
      .replace(',', '.')
      .toLowerCase();
  };

  const valuesEqual = (first, second) => {
    return normalize(first) === normalize(second);
  };

  const getProductUrl = (picker) => {
    return String(picker?.dataset?.url || '')
      .split('?')[0]
      .replace(/\/$/, '');
  };

  const getJsonUrl = (picker) => {
    const productUrl = getProductUrl(picker);

    if (!productUrl) return '';

    return productUrl.endsWith('.js')
      ? productUrl
      : `${productUrl}.js`;
  };

  const getGroups = (picker) => {
    return Array.from(
      picker.querySelectorAll('.product-form__input')
    ).filter((group) => {
      return group.querySelector(
        'select, input[type="radio"]'
      );
    });
  };

  const getSelectedValues = (groups) => {
    return groups.map((group) => {
      const select = group.querySelector('select');

      if (select) {
        return select.selectedOptions[0]?.value || '';
      }

      return (
        group.querySelector(
          'input[type="radio"]:checked'
        )?.value || ''
      );
    });
  };

  const getTargetValue = (target) => {
    if (target.tagName === 'SELECT') {
      return target.selectedOptions[0]?.value || '';
    }

    return target.value || '';
  };

  const loadProduct = async (picker) => {
    const productUrl = getProductUrl(picker);
    const jsonUrl = getJsonUrl(picker);

    if (!productUrl || !jsonUrl) return null;

    if (state.productCache.has(productUrl)) {
      return state.productCache.get(productUrl);
    }

    const request = fetch(jsonUrl, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Produktdaten konnten nicht geladen werden: ${response.status}`
          );
        }

        return response.json();
      })
      .catch((error) => {
        state.productCache.delete(productUrl);
        console.error('Magrion Variantenfilter:', error);
        return null;
      });

    state.productCache.set(productUrl, request);

    return request;
  };

  const getAvailableVariants = (product) => {
    if (!product || !Array.isArray(product.variants)) {
      return [];
    }

    return product.variants.filter((variant) => {
      return (
        variant.available !== false &&
        Array.isArray(variant.options)
      );
    });
  };

  const getVariantScore = (
    variant,
    selectedValues,
    activeIndex
  ) => {
    let score = 0;

    variant.options.forEach((value, index) => {
      if (index === activeIndex) return;

      if (valuesEqual(value, selectedValues[index])) {
        score += 1;
      }
    });

    return score;
  };

  const findBestVariant = (
    product,
    selectedValues,
    activeIndex,
    activeValue
  ) => {
    const candidates = getAvailableVariants(product).filter(
      (variant) => {
        return valuesEqual(
          variant.options[activeIndex],
          activeValue
        );
      }
    );

    if (!candidates.length) return null;

    candidates.sort((first, second) => {
      return (
        getVariantScore(
          second,
          selectedValues,
          activeIndex
        ) -
        getVariantScore(
          first,
          selectedValues,
          activeIndex
        )
      );
    });

    return candidates[0];
  };

  const selectValue = (group, value) => {
    const select = group.querySelector('select');

    if (select) {
      const options = Array.from(select.options);

      const matchingOption = options.find((option) => {
        return valuesEqual(option.value, value);
      });

      if (!matchingOption) return;

      options.forEach((option) => {
        option.selected = false;
        option.removeAttribute('selected');
      });

      matchingOption.selected = true;
      matchingOption.setAttribute(
        'selected',
        'selected'
      );

      return;
    }

    const radios = Array.from(
      group.querySelectorAll('input[type="radio"]')
    );

    const matchingRadio = radios.find((radio) => {
      return valuesEqual(radio.value, value);
    });

    if (!matchingRadio) return;

    radios.forEach((radio) => {
      radio.checked = false;
    });

    matchingRadio.checked = true;
  };

  const applyVariant = (picker, variant) => {
    if (!variant) return;

    const groups = getGroups(picker);

    variant.options.forEach((value, index) => {
      if (groups[index]) {
        selectValue(groups[index], value);
      }
    });
  };

  const getGroupChoices = (group) => {
    const select = group.querySelector('select');

    if (select) {
      return Array.from(select.options);
    }

    return Array.from(
      group.querySelectorAll('input[type="radio"]')
    );
  };

  const markChoice = (choice, available) => {
    /*
     * Das Hyper-Theme verwendet die Klasse "disabled",
     * um nicht passende Variantenwerte auszugrauen.
     * Die Schaltfläche bleibt anklickbar und kann dadurch
     * selbst zum nächsten Filter werden.
     */
    choice.classList.toggle('disabled', !available);

    choice.setAttribute(
      'aria-disabled',
      available ? 'false' : 'true'
    );

    if (choice.tagName === 'OPTION') {
      choice.disabled = false;
    }
  };

  const updateAvailability = (
    picker,
    product,
    activeIndex,
    activeValue
  ) => {
    const groups = getGroups(picker);
    const availableVariants = getAvailableVariants(product);

    if (
      !groups.length ||
      !availableVariants.length ||
      activeIndex < 0
    ) {
      return;
    }

    const matchingVariants = availableVariants.filter(
      (variant) => {
        return valuesEqual(
          variant.options[activeIndex],
          activeValue
        );
      }
    );

    groups.forEach((group, groupIndex) => {
      getGroupChoices(group).forEach((choice) => {
        const choiceValue = choice.value || '';

        /*
         * Die gerade verwendete Filtergruppe zeigt alle Werte,
         * die grundsätzlich in einer verfügbaren Variante vorkommen.
         */
        if (groupIndex === activeIndex) {
          const exists = availableVariants.some(
            (variant) => {
              return valuesEqual(
                variant.options[groupIndex],
                choiceValue
              );
            }
          );

          markChoice(choice, exists);
          return;
        }

        /*
         * Länge, Breite beziehungsweise Höhe werden passend
         * zum zuletzt angeklickten Maß angezeigt.
         */
        const compatible = matchingVariants.some(
          (variant) => {
            return valuesEqual(
              variant.options[groupIndex],
              choiceValue
            );
          }
        );

        markChoice(choice, compatible);
      });
    });
  };

  const preparePicker = async (picker) => {
    const productUrl = getProductUrl(picker);
    if (!productUrl) return;

    const product = await loadProduct(picker);
    if (!product || !picker.isConnected) return;

    const filter = state.activeFilters.get(productUrl);

    if (filter) {
      updateAvailability(
        picker,
        product,
        filter.index,
        filter.value
      );
    }
  };

  /*
   * Dieser Listener läuft vor der ursprünglichen Hyper-Logik.
   * So wird zuerst eine existierende Variantenkombination
   * eingestellt und danach von Hyper normal verarbeitet.
   */
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;

      if (
        !(target instanceof Element) ||
        !target.matches(
          'variant-selects select, variant-selects input[type="radio"]'
        )
      ) {
        return;
      }

      const picker = target.closest('variant-selects');
      if (!picker) return;

      const productUrl = getProductUrl(picker);
      const groups = getGroups(picker);
      const activeGroup = target.closest(
        '.product-form__input'
      );

      const activeIndex = groups.indexOf(activeGroup);
      const activeValue = getTargetValue(target);

      if (
        !productUrl ||
        activeIndex < 0 ||
        !activeValue
      ) {
        return;
      }

      state.activeFilters.set(productUrl, {
        index: activeIndex,
        value: activeValue
      });

      /*
       * Beim ersten Klick können die Produktdaten noch geladen
       * werden. Bei allen weiteren Klicks liegen sie im Cache.
       */
      loadProduct(picker).then((product) => {
        if (!product) return;

        const selectedValues = getSelectedValues(groups);

        const bestVariant = findBestVariant(
          product,
          selectedValues,
          activeIndex,
          activeValue
        );

        if (bestVariant && picker.isConnected) {
          applyVariant(picker, bestVariant);

          updateAvailability(
            picker,
            product,
            activeIndex,
            activeValue
          );
        }
      });
    },
    true
  );

  /*
   * Hyper ersetzt nach einer Variantenwahl die Auswahlfelder.
   * Der Observer erkennt die neuen Felder und wendet unseren
   * zuletzt gewählten Filter erneut an.
   */
  const observer = new MutationObserver((mutations) => {
    const newPickers = new Set();

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches('variant-selects')) {
          newPickers.add(node);
        }

        node
          .querySelectorAll?.('variant-selects')
          .forEach((picker) => {
            newPickers.add(picker);
          });
      });
    });

    newPickers.forEach((picker) => {
      preparePicker(picker);
    });
  });

  const start = () => {
    document
      .querySelectorAll('variant-selects')
      .forEach((picker) => {
        preparePicker(picker);
      });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();